import { css } from '@emotion/react';
import FontSizes from '@polestar/component-warehouse-react/tokens/font-sizes/FontSizes';
import LetterSpacings, { letterSpacingsValues, } from '@polestar/component-warehouse-react/tokens/letter-spacings/LetterSpacings';
import LineHeights, { lineHeightValues, } from '@polestar/component-warehouse-react/tokens/line-heights/LineHeights';
import reset from './reset.styles';
import { createCSSTransition, MotionDurations, MotionEasings, } from '@polestar/component-warehouse-react/tokens/motion/Motion';
import { hasScrollbarWidth } from '@polestar/component-warehouse-react/helpers/supports';
import MediaQueries from '@polestar/component-warehouse-react/tokens/media-queries/MediaQueries';
import { createCustomSpacing } from '@polestar/component-warehouse-react/tokens/spacings/Spacings';
import { MegaMenuCSSCustomProperties } from '@polestar/component-warehouse-react/components/mega-menu/MegaMenu';
import { megaMenuMinimalSafeAreaHeight } from '@polestar/component-warehouse-react/components/mega-menu/internal/MegaMenu.config';
import { OfferBarCSSCustomProperties } from '@polestar/component-warehouse-react/components/offer-bar/OfferBar';
import { ImporterBannerCSSCustomProperties } from '../../importer-banner-top/ImporterBannerTop';
import { responsiveStyles } from '@polestar/component-warehouse-react/helpers/responsiveStyle';
import dataTypeName from '@polestar/component-warehouse-react/dataTypeName';
const height = (rootSelector) => css `
  html,
  body,
  ${rootSelector} {
    height: 100%;
  }
`;
const backgroundColor = (theme, rootSelector, instantThemeTransition) => css(css `
      body,
      ${rootSelector} {
        background-color: ${theme.polestarApp.backgroundColor};
      }
    `, !instantThemeTransition &&
    css `
        ${rootSelector} {
          transition: ${createCSSTransition('background-color', MotionEasings.easeOut, MotionDurations.fast)};
        }
      `);
const text = (theme, rootSelector) => css `
  html {
    font-size: 100%;
  }

  div[data-version][data-type='${dataTypeName}'] {
    ${responsiveStyles({
    [MegaMenuCSSCustomProperties.barHeight]: {
        mobile: megaMenuMinimalSafeAreaHeight,
        tablet: createCustomSpacing(64 / 16),
        desktop: createCustomSpacing(72 / 16),
    },
})}
    ${MegaMenuCSSCustomProperties.subNavigationHeight}: 0px;
    ${MegaMenuCSSCustomProperties.menuTopOffset}: calc(var(${OfferBarCSSCustomProperties.blockSize}, 0px) + var(${ImporterBannerCSSCustomProperties.blockSize}, 0px));
    ${MegaMenuCSSCustomProperties.blockSize}: calc(var(${MegaMenuCSSCustomProperties.barHeight}, 0px) + var(${MegaMenuCSSCustomProperties.subNavigationHeight}, 0px));
    ${MegaMenuCSSCustomProperties.safeAreaHeight}: calc(var(${MegaMenuCSSCustomProperties.menuTopOffset}, 0px) + var(${MegaMenuCSSCustomProperties.barHeight}, 0px) + var(${MegaMenuCSSCustomProperties.subNavigationHeight}, 0px));
    ${MegaMenuCSSCustomProperties.maxSafeAreaHeight}: calc(var(${MegaMenuCSSCustomProperties.menuTopOffset}, 0px) + var(${MegaMenuCSSCustomProperties.barHeight}, 0px) + var(${MegaMenuCSSCustomProperties.subNavigationHeight}, 0px));
    ${MegaMenuCSSCustomProperties.hideHeader}: 0;
  }

  :lang(ko) {
    /* Needed for CJK languages: https://developer.mozilla.org/en-US/docs/Web/CSS/word-break#values
     Might want to use this also for Chinese and Japanese markets */
    word-break: keep-all;
  }

  ${rootSelector} {
    &,
    [dir='ltr'] {
      font-family: ${theme.polestarApp.fontFamily};
    }

    [dir='rtl'] {
      font-family: ${theme.polestarApp.fontFamilyRTL};
    }

    font-style: ${theme.polestarApp.fontStyle};
    font-weight: ${theme.polestarApp.fontWeight};
    color: ${theme.polestarApp.color};

    /* default (see html definition above) all text is 1rem (~16px), but FontSizes is different and
       therefore set here. LineHeights is the same as defined above and not set again.
    */
    font-size: ${FontSizes.body};
    line-height: ${LineHeights.small};
    letter-spacing: ${LetterSpacings.small};
  }

  input,
  select,
  textarea,
  button {
    font-family: inherit;
    font-size: inherit;
    line-height: inherit;
    letter-spacing: inherit;
  }

  strong,
  b {
    font-weight: 700;
  }

  /* Hide native focus styles, is replaced with global FocusProvider */
  ::-moz-focus-inner {
    border: none;
  }

  :focus {
    outline: 0;
    box-shadow: none;
  }

  :focus-visible {
    outline: 0;
    box-shadow: none;
  }
`;
const scrolling = css `
  .cw-disabled-scrolling {
    touch-action: none;
    overscroll-behavior: none;

    ${!hasScrollbarWidth &&
    css `
      overflow: hidden;
    `}
  }

  /* This is only needed on mobile and in special usecases where an input is inside a modal */
  .cw-scrolling-input-fix {
    ${MediaQueries.mobile} {
      position: fixed;
    }
  }
`;
/**
 * ensure that all CSS Custom Props are defined in all XXXFontSettings below to ensure no fallback is needed
 */
const ltrFontSettings = css `
  ${LineHeights.customProps.small}: ${lineHeightValues.ltr.small};
  ${LineHeights.customProps.large}: ${lineHeightValues.ltr.large};

  ${LetterSpacings.customProps.small}: ${letterSpacingsValues.ltr.small};
  ${LetterSpacings.customProps.large}: ${letterSpacingsValues.ltr.large};
`;
const rtlFontSettings = css `
  ${LineHeights.customProps.small}: ${lineHeightValues.rtl.small};
  ${LineHeights.customProps.large}: ${lineHeightValues.rtl.large};

  ${LetterSpacings.customProps.small}: ${letterSpacingsValues.rtl.small};
  ${LetterSpacings.customProps.large}: ${letterSpacingsValues.rtl.large};
`;
const arabicFontSettings = css `
  ${LineHeights.customProps.small}: ${lineHeightValues.arabic.small};
  ${LineHeights.customProps.large}: ${lineHeightValues.arabic.large};

  ${LetterSpacings.customProps.small}: ${letterSpacingsValues.rtl.small};
  ${LetterSpacings.customProps.large}: ${letterSpacingsValues.rtl.large};
`;
const directionFontSettingsOverrides = css `
  /* nothing set, fallback */
  :root:not([dir]) *,
  /* determine LTR/RTL combinations: */
  :root[dir='ltr'],
  :root [dir='ltr'] {
    ${ltrFontSettings};
  }

  :root[lang='he'],
  :root [lang='he'] {
    ${rtlFontSettings};
  }

  :root[lang='ar'],
  :root [lang='ar'] {
    ${arabicFontSettings};
  }
`;
/**
 * Use this for component directional or language specific style overrides
 *
 * ```ts
 * componentFontSettingsOverrides(
 *    // ltr
 *    css`
 *      ${property}: ${values.ltr.value};
 *    `,
 *    // rtl
 *    css`
 *      ${property}: ${values.rtl.value};
 *    `,
 *    // arabic
 *    css`
 *      ${property}: ${values.arabic.value};
 *    `,
 * ```
 */
export const componentFontSettingsOverrides = (ltr, rtl, arabic) => css `
  [dir='ltr'] *:not(:has(*[dir])) &,
  [dir='ltr'] > &,
  [dir='ltr']:not(:has(*[dir])) & {
    ${ltr};
  }

  [lang='he'] > &,
  [lang='he']:not(:has(*[lang])) & {
    ${rtl};
  }

  [lang='ar'] > &,
  [lang='ar']:not(:has(*[lang])) & {
    ${arabic};
  }
`;
const globalStyles = ({ rootSelector, theme, instantThemeTransition, }) => css(reset(rootSelector), height(rootSelector), backgroundColor(theme, rootSelector, instantThemeTransition), text(theme, rootSelector), scrolling, directionFontSettingsOverrides);
export default globalStyles;
